import { inject, Injectable, OnDestroy } from "@angular/core";
import { Router } from "@angular/router";
import { CldrIntlService, IntlService } from "@progress/kendo-angular-intl";

import { Subject } from "rxjs";

import { IBaseT1Card, IDashboardModel, isCardGrouped, isiFirmTaxDashboard } from "@app/views-interfaces";
import { differenceInDays, differenceInSeconds, format, formatDistanceToNow, fromUnixTime, setDefaultOptions, startOfDay } from "date-fns";
import { toZonedTime } from "date-fns-tz";
import { enCA, frCA } from "date-fns/locale";
import { ICategories } from "src/app/views-interfaces/categories.interface";
import { ITaxReturnStatus } from "src/app/views-interfaces/tax-return-status-model.interface";
import { IMergedStepsModel } from "../../views-interfaces/steps-model.interface";
import { IT1Tags } from "../../views-interfaces/t1-tags.interface";
import { AnalyticsService } from "../analytics-service/analytics.service";
import { ApplicationsService } from "../applications-service/applications.service";
import { AuthService } from "../auth/auth.service";
import { CardViewService } from "../card-view-service/card-view.service";
import { DashboardsService } from "../dashboards-service/dashboards.service";
import statuses from "../eFileStatuses.json";
import { FirmsService } from "../firms-service/firms.service";
import { IframeMessagesService } from "../iframe-messages-service/iframe-messages.service";
import { LocalStorageService } from "../local-storage/local-storage.service";
import { MappingOfStatusesService } from "../mapping-of-statuses-service/mapping-of-statuses.service";
import tags from "../T1ResultContentType.json";
import { TaxReturnStatusesService } from "../tax-return-statuses-service/tax-return-statuses.service";
import { UserDashboardSettingsService } from "../user-dashboard-settings-service/user-dashboard-settings.service";
import { UsersService } from "../users-service/users.service";
import { UtilsService } from "../utils/utils.service";
import { IFirmRolesService, IfirmTaxCardsService, IFirmUserRolesService, IHelpersService, StepsService } from "./../";
import { CategoriesService } from "./../categories-service/categories.service";

@Injectable()
export class HelpersService implements IHelpersService, OnDestroy {
    public static readonly secondsInAYear = 31536000;

    private readonly iFirmRolesService = inject(IFirmRolesService);
    private readonly iFirmUserRolesService = inject(IFirmUserRolesService);
    private readonly router = inject(Router);
    private readonly stepsService = inject(StepsService);
    private readonly analyticsService = inject(AnalyticsService);
    authService = inject(AuthService);
    cardViewService = inject(CardViewService);
    categoriesService = inject(CategoriesService);
    dashboardsService = inject(DashboardsService);
    firmsService = inject(FirmsService);
    iframeMessagesService = inject(IframeMessagesService);
    intlService = inject(IntlService);
    mappingOfStatusesService = inject(MappingOfStatusesService);
    taxReturnStatusesService = inject(TaxReturnStatusesService);
    userDashboardSettingsService = inject(UserDashboardSettingsService);
    usersService = inject(UsersService);
    ifirmTaxCardsService = inject(IfirmTaxCardsService);
    private readonly applicationsService = inject(ApplicationsService);

    placeLabelIsEditProgressEmitter = new Subject<boolean>();
    placeLabelIsEditProgressEvent$ = this.placeLabelIsEditProgressEmitter.asObservable();
    spinnerRequestedEmitter = new Subject<boolean>();
    spinnerRequested$ = this.spinnerRequestedEmitter.asObservable();
    private readonly langChangeEmitter = new Subject<string>();
    langChangeEvent$ = this.langChangeEmitter.asObservable();
    microServiceErrorEmitter = new Subject<any>();
    microServiceErrorEvent$ = this.microServiceErrorEmitter.asObservable();
    cardDownloadEmitter = new Subject<boolean>();
    cardDownloadEvent$ = this.cardDownloadEmitter.asObservable();
    unauthorizedNavigationEmitter = new Subject<any>();
    unauthorizedNavigationEvent$ = this.unauthorizedNavigationEmitter.asObservable();
    noUdsNavigationEmitter = new Subject<any>();
    noUdsNavigationEvent$ = this.noUdsNavigationEmitter.asObservable();
    mappingColumnChangeEmitter = new Subject<any>();
    mappingColumnChangeEvent$ = this.mappingColumnChangeEmitter.asObservable();
    suppressSpinnerEmitter = new Subject<any>();
    suppressSpinnerEvent$ = this.suppressSpinnerEmitter.asObservable();
    refreshFiltersEmitter = new Subject<any>();
    refreshFiltersEvent$ = this.refreshFiltersEmitter.asObservable();
    favouriteFiltersEditModeEmitter = new Subject<any>();
    favouriteFiltersEditModeEvent$ = this.favouriteFiltersEditModeEmitter.asObservable();
    favouriteFiltersPendingChangesEmitter = new Subject<any>();
    favouriteFiltersPendingChangesEvent$ = this.favouriteFiltersPendingChangesEmitter.asObservable();
    revertToFavoriteFilterEmitter = new Subject<void>();
    revertToFavoriteFilterEvent$ = this.revertToFavoriteFilterEmitter.asObservable();
    columnWrapperCalPreviousTop = 0;
    private T1Tags: IT1Tags[] = [];

    selectedCardsIds = [];
    isOnline = true;
    public lang: string;
    public eFileStatuses: object;
    private currentDashboard: IDashboardModel;
    private categories: ICategories[];
    private taxReturnStatuses: ITaxReturnStatus[];

    private mergedUserDataPromise: Promise<void> | undefined;
    stepsMerged = false;
    categoriesInitialized = false;

    constructor() {
        if (localStorage.getItem("lang") === null) {
            LocalStorageService.setItem("lang", "en-CA");
        }

        const locale = this.getLang() === "en-CA" ? enCA : frCA;
        setDefaultOptions({ locale });

        this.dashboardsService.currentDashboardChanged$.subscribe((dashboardId) => {
            this.currentDashboard = this.dashboardsService.Dashboards.find((it) => it.Id === dashboardId);
            this.refreshUserDataMerged();
            this.refreshStepsMerged();
            this.refreshCategoriesInitialized();
        });

        //assign eFile statuses for cards
        this.eFileStatuses = statuses;

        this.dashboardsService.dashboardsLoadedEvent$.subscribe(async () => {
            this.currentDashboard = await this.dashboardsService.getCurrentDashboard();
            this.refreshUserDataMerged();
            this.refreshStepsMerged();
        });

        this.userDashboardSettingsService.userDashboardSettingsLoadedEvent$.subscribe(() => {
            this.refreshUserDataMerged();
        });

        this.stepsService.stepsLoadedEvent$.subscribe(() => {
            this.refreshUserDataMerged();
            this.refreshStepsMerged();
        });

        this.taxReturnStatusesService.taxReturnStatusesLoadedEvent$.subscribe(() => {
            this.refreshStepsMerged();
        });

        this.categoriesService.categoriesLoadedEvent$.subscribe(() => {
            this.refreshCategoriesInitialized();
        });
    }

    refreshUserDataMerged() {
        this.userDashboardSettingsService.refresh();
        this.mergedUserDataPromise = undefined;
    }

    refreshStepsMerged() {
        this.stepsMerged = false;
    }

    refreshCategoriesInitialized() {
        this.categoriesInitialized = false;
    }

    getLang(): string {
        if (!this.lang) {
            this.lang = localStorage.getItem("lang");
            if (!this.lang) {
                this.lang = "en-CA";
            }
        }
        return this.lang;
    }

    setLang(lang: string): void {
        if (lang && (lang === "en-CA" || lang === "fr-CA")) {
            this.lang = lang;
            LocalStorageService.setItem("lang", lang);
            const locale = lang === "en-CA" ? enCA : frCA;
            setDefaultOptions({ locale });
            (this.intlService as CldrIntlService).localeId = lang;
            this.langChangeEmitter.next(lang);
        }
    }

    dateFrom(value: any): string {
        return formatDistanceToNow(new Date(value), { addSuffix: true });
    }

    dateFromUnix(value: any): string {
        return formatDistanceToNow(fromUnixTime(value), { addSuffix: true });
    }

    daysDifferenceUnix(value: any, includeToday = false, latestDate = Math.round(this.NowUTC().getTime() / 1000)): number {
        const laterDate = fromUnixTime(latestDate);
        const earlierDate = fromUnixTime(value);
        let days = differenceInDays(laterDate, earlierDate);
        if (includeToday) {
            days++;
        }
        return days;
    }

    daysDifferenceUnixWithSkipTime(value: any, includeToday = false, latestDate = Math.round(this.NowUTC().getTime() / 1000)): number {
        const laterDateUTC = toZonedTime(fromUnixTime(latestDate), "UTC");
        const earlierDateUTC = toZonedTime(fromUnixTime(value), "UTC");

        const aa = startOfDay(laterDateUTC);
        const bb = startOfDay(earlierDateUTC);

        let days = differenceInDays(aa, bb);
        if (includeToday) {
            days++;
        }

        return days;
    }

    secondsDifferenceUnix(value: any, latestDate = Math.round(Date.now() / 1000)): number {
        const laterDate = fromUnixTime(latestDate);
        const earlierDate = fromUnixTime(value);
        return differenceInSeconds(laterDate, earlierDate);
    }

    daysDifferenceFromFutureUnix(value: any, includeToday = false): number {
        const currentDate = fromUnixTime(Math.round(Date.now() / 1000));
        const futureDate = fromUnixTime(value);
        let days = differenceInDays(futureDate, currentDate);
        if (includeToday) {
            days++;
        }
        return days;
    }

    date(value: any, dateFormat: any): string {
        return format(new Date(value), dateFormat);
    }

    dateUnix(value: any, dateFormat: any): string {
        return format(fromUnixTime(value), dateFormat);
    }

    getDateNeeded(cardData: any): Date {
        return cardData.dateNeeded
            ? this.getAdjustedDateFromUTC(cardData.dateNeeded)
            : cardData.deadLine
              ? this.getAdjustedDateFromUTC(cardData.deadLine)
              : null;
    }

    getAdjustedDateFromUTC(utcTimestamp) {
        return this.getUnixDate(utcTimestamp + (12 * 60 * 60 + new Date().getTimezoneOffset() * 60));
    }

    getUnixDate(value: number): Date {
        return fromUnixTime(value);
    }

    bytesArrToBase64(arr) {
        const abc = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/"; // base64 alphabet
        const bin = (n) => n.toString(2).padStart(8, 0); // convert num to 8-bit binary string
        const l = arr.length;
        let result = "";

        for (let i = 0; i <= (l - 1) / 3; i++) {
            const c1 = i * 3 + 1 >= l; // case when "=" is on end
            const c2 = i * 3 + 2 >= l; // case when "=" is on end
            const chunk = bin(arr[3 * i]) + bin(c1 ? 0 : arr[3 * i + 1]) + bin(c2 ? 0 : arr[3 * i + 2]);
            const r = chunk.match(/.{1,6}/g).map((x, j) => (j == 3 && c2 ? "=" : j == 2 && c1 ? "=" : abc[+("0b" + x)]));
            result += r.join("");
        }
        return result;
    }

    // This function fetches all the data for every user and stores them in another property for easy use throughout the application
    async mergeUserData() {
        if (!this.mergedUserDataPromise) {
            this.mergedUserDataPromise = this._mergeUserData();
        }
        return this.mergedUserDataPromise;
    }

    async _mergeUserData() {
        await Promise.all([this.usersService.getUsersList(), this.iFirmRolesService.getiFirmRoles(), this.iFirmUserRolesService.getiFirmUserRoles()]);
        if (this.usersService.Users) {
            // Retrieve dashboards
            this.userDashboardSettingsService.getUserDashboardSettingsByUserId(this.getCurrentUser().sub);

            // Process Tracking Users
            this.usersService.Users.forEach((user) => {
                // Store user in merged users property
                this.usersService.MergedUserData[user.Id] = UtilsService.recursiveDeepCopy(user);
                // Set default values to null
                this.usersService.MergedUserData[user.Id].Roles = null;
                this.usersService.MergedUserData[user.Id].UserDashboardSettings = null;
                // Retrieve user dashboard settings
                const userDashboardSettings = this.userDashboardSettingsService.getUserDashboardSettingsByUserId(user.Id);
                if (userDashboardSettings && userDashboardSettings.length > 0) {
                    this.usersService.MergedUserData[user.Id].UserDashboardSettings = userDashboardSettings;
                    this.usersService.MergedUserData[user.Id].Roles = this.iFirmUserRolesService.getRolesByUser(user.Id);
                }
            });
        }

        // Process iFirm User
        if (this.getCurrentUser().role && this.getCurrentUser().role.indexOf("IFirmUser") > -1) {
            // Store user in merged users property
            this.usersService.MergedUserData[this.authService.currentUser.sub] = UtilsService.recursiveDeepCopy(
                this.usersService.getUserById(this.authService.currentUser.sub),
            );
            if (this.usersService.MergedUserData[this.authService.currentUser.sub]) {
                // No iFirm Users on User Settings
                // Set default values to null
                this.usersService.MergedUserData[this.authService.currentUser.sub].Roles = null;
                this.usersService.MergedUserData[this.authService.currentUser.sub].UserDashboardSettings = null;
            }
        }
    }

    async mergeSteps() {
        if (this.stepsMerged) {
            return;
        }

        if (!this.dashboardsService.dashboardsLoaded) {
            await this.dashboardsService.getDashboardsList();
        }
        await this.iFirmRolesService.getiFirmRoles();
        this.taxReturnStatuses = await this.taxReturnStatusesService.getTaxReturnStatusesList();
        this.mappingOfStatusesService.Steps = await this.stepsService.getStepsList();
        const steps = UtilsService.recursiveDeepCopy(this.mappingOfStatusesService.Steps);

        steps
            .filter((mergedStep) => mergedStep.Statuses !== undefined && mergedStep.Statuses.length > 0)
            .map((mergedStep) => mergedStep.Statuses)
            .flat()
            .map((status) => {
                status.Role = this.iFirmRolesService.iFirmRoles.find(({ Id }) => Id === status.RoleId);
                status.TaxReturnStatus = this.taxReturnStatuses.find(({ Id }) => Id === status.TaxReturnStatusId);
                delete status.RoleId;
                delete status.TaxReturnStatusId;
            });

        this.storeMergedSteps(steps);
        this.stepsMerged = true;
    }

    storeMergedSteps(steps: IMergedStepsModel[]) {
        steps.sort(function compare(a, b) {
            if (a.Order < b.Order) {
                return -1;
            }
            if (a.Order > b.Order) {
                return 1;
            }
            return 0;
        });
        this.mappingOfStatusesService.MergedSteps = steps;
        LocalStorageService.setItem("mergedSteps_" + this.dashboardsService.getCurrentDashboardId(), JSON.stringify(steps));
    }

    getMergedSteps(): IMergedStepsModel[] {
        if (this.mappingOfStatusesService.MergedSteps !== undefined) {
            return this.mappingOfStatusesService.MergedSteps;
        }
        const mergedSteps = localStorage.getItem("mergedSteps_" + this.dashboardsService.getCurrentDashboardId());
        if (mergedSteps !== null) {
            try {
                return JSON.parse(mergedSteps).sort(function compare(a, b) {
                    if (a.Order < b.Order) {
                        return -1;
                    }
                    if (a.Order > b.Order) {
                        return 1;
                    }
                    return 0;
                });
            } catch {
                return null;
            }
        }
    }

    storeFilterSelection(filter) {
        const currentFilters = this.getFilterSelection();
        let match = false;
        currentFilters.forEach((currentFilter, i) => {
            if (currentFilter.internalName === filter.internalName) {
                match = true;
                currentFilters[i] = filter;
                //clear users filter if Roles filter clear all initiated
                if (filter.internalName === "Roles") {
                    const index = currentFilters.findIndex((x) => x.name === "FiltersSidebar.Labels.Users");
                    if (filter?.selectedValues?.length == 0) {
                        if (index != -1) {
                            currentFilters[index].selectedValuesGroupBy = [];
                        }
                    } else if (index != -1) {
                        //on Roles filter changes, update the users filter local storage

                        if (currentFilters[index].selectedValuesGroupBy && currentFilters[index].selectedValuesGroupBy.length > 0) {
                            currentFilters[index].selectedValuesGroupBy = currentFilters[index].selectedValuesGroupBy.filter(function (element) {
                                return filter?.selectedValues.indexOf(element.subcategory) !== -1;
                            });
                        }
                    }
                }
            }
        });
        if (!match) {
            currentFilters.push(filter);
        }
        this.analyticsService.track("Filters are changed", {
            Filters: JSON.stringify(this.getFilterInformationForAnalytics(currentFilters)),
        });
        LocalStorageService.setItem("filters_t1_" + this.dashboardsService.getCurrentDashboardId(), JSON.stringify(currentFilters));
    }

    getFilterSelection() {
        const currentFilters = localStorage.getItem("filters_t1_" + this.dashboardsService.getCurrentDashboardId());
        if (currentFilters !== null) {
            try {
                return JSON.parse(currentFilters);
            } catch {
                return [];
            }
        }
        return [];
    }

    storeAlertsFilterSelection(filter, viewType) {
        const currentFilters = this.getAlertsFilterSelection(viewType);
        let match = false;
        currentFilters.forEach((currentFilter, i) => {
            if (currentFilter.internalName === filter.internalName) {
                match = true;
                currentFilters[i] = filter;
            }
        });
        if (!match) {
            currentFilters.push(filter);
        }
        LocalStorageService.setItem("Alerts_" + viewType + "_" + this.dashboardsService.getCurrentDashboardId(), JSON.stringify(currentFilters));
    }

    getAlertsFilterSelection(viewType) {
        const currentFilters = localStorage.getItem("Alerts_" + viewType + "_" + this.dashboardsService.getCurrentDashboardId());
        if (currentFilters !== null) {
            try {
                return JSON.parse(currentFilters);
            } catch {
                return [];
            }
        }
        return [];
    }

    getSortSelection(viewType) {
        const currentFilters = localStorage.getItem("SortBy_" + viewType + "_" + this.dashboardsService.getCurrentDashboardId());
        if (currentFilters !== null) {
            try {
                return JSON.parse(currentFilters);
            } catch {
                return [];
            }
        }
        return [];
    }

    storeSortSelection(filter, viewType) {
        if (viewType) {
            if (filter.internalName === "SortBy" || filter.internalName === "SortByAscending" || filter.internalName === "Order") {
                const currentFilters = this.getSortSelection(viewType);
                let match = false;
                currentFilters.forEach((currentFilter, i) => {
                    if (currentFilter.internalName === filter.internalName) {
                        match = true;
                        currentFilters[i] = filter;
                    }
                });
                if (!match) {
                    currentFilters.push(filter);
                }
                this.analyticsService.track("Filters are changed", {
                    Filters: JSON.stringify(this.getFilterInformationForAnalytics(currentFilters)),
                });
                LocalStorageService.setItem(
                    "SortBy_" + viewType + "_" + this.dashboardsService.getCurrentDashboardId(),
                    JSON.stringify(currentFilters),
                );
            } else if (filter.internalName === "Alerts") {
                this.storeAlertsFilterSelection(filter, viewType);
            }
        }
    }

    getFilterInformationForAnalytics(currentFilters) {
        return currentFilters.map((item) => {
            return {
                [item.internalName]: item.type == "select" && item.selectedValues ? item.selectedValues : item.selectedValue,
            };
        });
    }

    getT1Tags() {
        if (!this.T1Tags || this.T1Tags.length === 0) {
            this.T1Tags = this.prepareTagModel(tags);
        }
        return this.T1Tags;
    }

    prepareTagModel(T1Tags): any {
        return T1Tags.map((tag) => {
            return {
                Id: tag.Id,
                Name: {
                    "en-CA": tag.CaptionEnglish,
                    "fr-CA": tag.CaptionFrench,
                },
                Description: {
                    "en-CA": tag.DescriptionEnglish,
                    "fr-CA": tag.DescriptionFrench,
                },
                BackgroundColor: tag.BackgrounColor,
                ForegroundColor: tag.ForegroundColor,
                TaxpayerCell: tag.TaxpayerCell,
                TaxpayerPrevYearCell: tag.TaxpayerPrevYearCell,
                YearToCheck: tag.YearToCheck,
            };
        });
    }

    getTagsDataForCard(cardData) {
        const returnData = [];
        cardData.tags.forEach((tagId) => {
            const tagData = this.getT1Tags().find((tag) => {
                return tag.Id === tagId;
            });
            if (tagData) {
                returnData.push(tagData);
            }
        });
        return returnData;
    }

    getClientFileCount(cardData) {
        if (cardData.members && isCardGrouped(cardData, this.currentDashboard)) {
            return cardData.members.length + 1;
        }
        return 1;
    }

    checkCardStepWarning(result, cardData) {
        const step = this.mappingOfStatusesService.getStepFromStatusId(cardData.return.statusId);
        if ((!step || step.State !== "completed") && this.currentDashboard?.DaysInStepWarning) {
            return result.daysSinceInStep >= this.currentDashboard.DaysInStepWarning;
        }
        return false;
    }

    checkCardStepAlert(result, cardData) {
        const step = this.mappingOfStatusesService.getStepFromStatusId(cardData.return.statusId);
        if ((!step || step.State !== "completed") && this.currentDashboard?.DaysInStepAlert) {
            return result.daysSinceInStep >= this.currentDashboard.DaysInStepAlert;
        }
        return false;
    }

    checkCardDeadlineWarning(result, cardData) {
        const step = this.mappingOfStatusesService.getStepFromStatusId(cardData.return.statusId);
        if ((!step || step.State !== "completed") && this.currentDashboard?.DaysBeforeDeadlineWarning) {
            return (
                result.deadlineDate &&
                (this.currentDashboard.DaysBeforeDeadlineWarning >= Math.abs(result.deadlineDaysFromNow) || result.deadlineDaysFromNow > 0)
            );
        }
        return false;
    }

    checkCardDeadlineAlert(result, cardData) {
        const step = this.mappingOfStatusesService.getStepFromStatusId(cardData.return.statusId);
        if ((!step || step.State !== "completed") && this.currentDashboard?.DaysBeforeDeadlineAlert) {
            return (
                result.deadlineDate &&
                (this.currentDashboard.DaysBeforeDeadlineAlert >= Math.abs(result.deadlineDaysFromNow) || result.deadlineDaysFromNow > 0)
            );
        }
        return false;
    }

    checkCardEFileStatusAlert(cardData: IBaseT1Card) {
        let isAlert = false;
        if (cardData.efileStatuses) {
            isAlert =
                cardData.efileStatuses.fedEfileStatus === 2 ||
                cardData.efileStatuses.fedEfileStatus === 4 ||
                cardData.efileStatuses.provEfileStatus === 2 ||
                cardData.efileStatuses.provEfileStatus === 4;
        }
        return isAlert;
    }

    getAssignedUserForCardT1(cardData: IBaseT1Card, columnData) {
        const userMappings = cardData.roleUserMapping;
        let user = null;
        if (userMappings) {
            userMappings.forEach((userMapping) => {
                if (columnData.statuses) {
                    columnData.statuses.forEach((status) => {
                        if (status.Role && status.TaxReturnStatus) {
                            if (status.Role.Id && status.Role.Id === userMapping.roleId && cardData.return.statusId === status.TaxReturnStatus.Id) {
                                user = this.usersService.getUserById(userMapping.userId);
                                if (!user) {
                                    user = this.iFirmUserRolesService.iFirmUserRoles.find(
                                        (ur) => ur.UserId == userMapping.userId && ur.RoleId == userMapping.roleId,
                                    );
                                    // We expect a Tracking user comming out of this function, but faking it by adding 'Id property seems to work...?
                                    // We should probably change how we do that.
                                    if (user) {
                                        user.Id = user.UserId;
                                    }
                                }
                            }
                        }
                    });
                }
            });
        }
        return user;
    }

    getCurrentUser() {
        return this.authService.currentUser;
    }

    setColumnWrapperHeight() {
        const _card_view_counters_section_wrapper = document.getElementsByClassName("wk-card-view__counters-section--wrapper");
        const _wk_filters_bar__cnt = document.getElementsByClassName("wk-filters-bar__cnt");
        const _card_view_columns_wrapper = document.getElementsByClassName("wk-card-view__columns--wrapper");
        const _wk_active_filters_wrapper = document.getElementsByClassName("wk-active-filters--wrapper");

        if (_card_view_counters_section_wrapper.length > 0) {
            if (_wk_filters_bar__cnt.length > 0) {
                (_card_view_counters_section_wrapper[0] as HTMLElement).style.width = `calc(100% - ${_wk_filters_bar__cnt[0].clientWidth + 1}px)`;
            }
        }
        if (_card_view_columns_wrapper.length > 0) {
            if (_wk_active_filters_wrapper.length > 0) {
                (_card_view_columns_wrapper[0] as HTMLElement).style.height = `calc(100% - 157px - ${_wk_active_filters_wrapper[0].clientHeight}px)`;
            }
        }
    }

    storeRemovedSelectedFilters(data) {
        LocalStorageService.setItem("removedSelectedFilters_" + this.dashboardsService.getCurrentDashboardId(), JSON.stringify(data));
    }

    getRemovedSelectedFilters() {
        const data = localStorage.getItem("removedSelectedFilters_" + this.dashboardsService.getCurrentDashboardId());
        if (data !== null) {
            try {
                return JSON.parse(data);
            } catch {
                return [];
            }
        }
        return [];
    }

    sortStatusHistoryAscending(a, b) {
        if (a.statusDate > b.statusDate) {
            return 1;
        }
        if (a.statusDate < b.statusDate) {
            return -1;
        }
        return 0;
    }

    sortStatusHistoryDescending(a, b) {
        if (a.statusDate > b.statusDate) {
            return -1;
        }
        if (a.statusDate < b.statusDate) {
            return 1;
        }
        return 0;
    }

    formatPhoneNumber(s) {
        const s2 = ("" + s).replaceAll(/\D/g, "");
        const m = RegExp(/^(\d{3})(\d{3})(\d{4})$/).exec(s2);
        return !m ? null : m[1] + "-" + m[2] + "-" + m[3];
    }

    NowUTC() {
        const now = new Date();
        return new Date(now.getTime() + now.getTimezoneOffset() * 60000);
    }

    async changeDashboard(dashboardId: string, fromGuard = false) {
        const dashboardIdChanged = dashboardId !== this.dashboardsService.getCurrentDashboardId();
        if (dashboardIdChanged || fromGuard) {
            if ((await this.applicationsService.getAccessibleDashboards()).find((ad) => ad.Id === dashboardId)) {
                // Only tear down / rebuild connection-scoped state when the dashboard actually changes.
                // A guard re-run for the same dashboard (e.g. loading a card via the 'find' endpoint)
                // must not emit the change events, otherwise SignalR disconnects and reconnects to the
                // same hub (id -> null -> id churn through the merge/switchMap stream).
                if (dashboardIdChanged) {
                    this.dashboardsService.dashboardChangeEmitter.next();
                    this.selectedCardsIds = [];
                    this.cardDownloadEmitter.next(true);
                    if (this.cardViewService.currentViewType === "assignee") {
                        this.router.navigate(["/"]);
                    }
                    this.dashboardsService.setCurrentDashboardId(dashboardId);
                }
                return true;
            } else {
                this.changeDashboard((await this.applicationsService.getAccessibleDashboards())[0].Id, fromGuard);
                this.unauthorizedNavigationEmitter.next(dashboardId);
            }
        } else {
            return true;
        }
    }

    sendSetFrameHeight(height: number) {
        if (height < 675) {
            height = 675;
        }
        this.iframeMessagesService.postMessage("setFrameHeight", {
            frameHeight: height,
        });
    }

    suppressSpinner() {
        this.suppressSpinnerEmitter.next(true);
    }

    refreshFilters() {
        this.refreshFiltersEmitter.next(true);
    }

    async initializeCategories() {
        // TODO this.categories is no longer used I think. We should remove and ensure no side-effect.
        if (!this.categoriesInitialized) {
            this.categories = await this.categoriesService.getCategoriesList();
            this.categoriesInitialized = true;
        }
    }

    convertMillisecondsToSeconds(dateInput: number): number {
        return dateInput / 1000;
    }

    /**
     * Encodes a calendar date (its year/month/day) as a UTC-noon Unix timestamp in seconds.
     * Use for date-only fields (e.g. target start/end dates) where only the calendar date matters
     * and the time zone does not. Anchoring at noon UTC (not midnight) keeps the value 12 hours away
     * from either date boundary, so it renders as the same calendar date regardless of the viewer's
     * time zone and regardless of whether the consumer formats it in UTC or local time (browser,
     * audit trail, grid, filters, job details). This avoids the off-by-one-day shift seen when a
     * midnight value is read in a different UTC offset.
     */
    convertDateOnlyToSeconds(date: Date): number {
        return Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 12, 0, 0) / 1000;
    }

    ngOnDestroy(): void {
        this.placeLabelIsEditProgressEmitter.complete();
        this.spinnerRequestedEmitter.complete();
        this.langChangeEmitter.complete();
        this.microServiceErrorEmitter.complete();
        this.cardDownloadEmitter.complete();
        this.unauthorizedNavigationEmitter.complete();
        this.noUdsNavigationEmitter.complete();
        this.mappingColumnChangeEmitter.complete();
        this.suppressSpinnerEmitter.complete();
        this.refreshFiltersEmitter.complete();
        this.favouriteFiltersEditModeEmitter.complete();
        this.favouriteFiltersPendingChangesEmitter.complete();
        this.revertToFavoriteFilterEmitter.complete();
    }
}
